import React, { useEffect, useMemo, useState } from 'react';
import _ from '@lodash';
import { useDispatch, useSelector } from 'react-redux';
import FuseAnimate from '@fuse/core/FuseAnimate';
import FuseLoading from '@fuse/core/FuseLoading';
import FusePageCarded from '@fuse/core/FusePageCarded';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Icon from '@material-ui/core/Icon';
import IconButton from '@material-ui/core/IconButton';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import { useForm } from '@fuse/hooks';
import { formatUsPhone } from 'app/constants/formFields';
import useUnsavedChangesGuard from 'app/main/utils/useUnsavedChangesGuard';
import withReducer from 'app/store/withReducer';
import { closeDialog, openDialog } from 'app/store/fuse/dialogSlice';
import reducer from 'app/main/settings/store';
import { isFirstLogin } from 'app/main/settings/store/userSlice';
import { useHistory } from 'react-router-dom';
import Snackbar from '@material-ui/core/Snackbar';
import Alert from '@material-ui/lab/Alert';
import UserInfoTab from '../components/UserInfoTab';
import BasicInfoTab from './tabs/BasicInfoTab';
import ProfessionalInfoTab from './tabs/ProfessionalInfoTab';
import MarketingTab from './tabs/MarketingTab';
import FinancialsTab from './tabs/FinancialsTab';
import ContactCard from '../contacts/ContactCard';
import { userToCardContact } from 'app/main/utils/vcard';

const useStyles = makeStyles(theme => ({
  avatarUpload: {
    transitionProperty: 'box-shadow',
    transitionDuration: theme.transitions.duration.short,
    transitionTimingFunction: theme.transitions.easing.easeInOut
  },
  contentCard: {
    marginBottom: '200px',
    maxWidth: '1000px',
    width: '100%',
    marginLeft: 'auto',
    marginRight: 'auto'
  }
}));

function Userprofile() {
  const dispatch = useDispatch();
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const [tabValue, setTabValue] = useState(0);
  const [savedSuccessfully, setSavedSuccessfully] = useState(false);
  const [serverError, setServerError] = useState(false);
  // The record is reshaped on the way in, so it is the wrong thing to diff against.
  const [baseline, setBaseline] = useState(null);

  const loggedUser = useSelector(state => state.auth.user.data);
  const cardContact = useMemo(() => loggedUser && userToCardContact(loggedUser), [loggedUser]);
  const { form, setForm, setInForm, handleChange } = useForm(null);

  useEffect(() => {
    if (loggedUser) {
      const hydrated = {
        ...loggedUser,
        stateLicenses: loggedUser.stateLicenses?.length ? loggedUser.stateLicenses : [{ state: '', number: '' }],
        nationalLicense: loggedUser.nationalLicense || '',
        firstName: loggedUser.firstName || '',
        lastName: loggedUser.lastName || '',
        email: loggedUser.email || '',
        phoneNumber: formatUsPhone(loggedUser.phoneNumber),
        avatar: loggedUser.avatar || '',
        password: '',
        birthday: loggedUser.birthday ? new Date(loggedUser.birthday).toISOString().split('T')[0] : '',
        businessPhoneNumber: formatUsPhone(loggedUser.businessPhoneNumber),
        iloanEmail: loggedUser.iloanEmail || loggedUser.email || '',
        nmlsId: loggedUser.nmlsId || '',
        website: loggedUser.website || '',
        streetAddress2: loggedUser.streetAddress2 || '',
        city: loggedUser.city || '',
        state: loggedUser.state || '',
        zipcode: loggedUser.zipcode || '',
        emergencyContactName: loggedUser.emergencyContactName || '',
        emergencyContactPhone: formatUsPhone(loggedUser.emergencyContactPhone),
        startingDate: loggedUser.startingDate ? new Date(loggedUser.startingDate).toISOString().split('T')[0] : ''
      };
      setForm(hydrated);
      setBaseline(hydrated);
    }
  }, [loggedUser, setForm]);

  const isDirty = Boolean(form && baseline && !_.isEqual(form, baseline));
  const canSave = Boolean(form && form.firstName?.trim() && form.lastName?.trim());
  useUnsavedChangesGuard({
    isDirty,
    canSave,
    saveBlockedHint: 'A first and last name are required before saving.',
    onSave: () => handleSave()
  });

  const handleSave = () => {
    const userToSave = {
      ...form,
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      phoneNumber: form.phoneNumber || '',
      birthday: form.birthday ? new Date(form.birthday) : null,
      startingDate: form.startingDate ? new Date(form.startingDate) : null
    };

    dispatch(isFirstLogin(userToSave)).then(({ payload }) => {
      if (payload && payload.id) {
        setSavedSuccessfully(true);
      } else {
        setServerError(true);
      }
    }).catch(() => setServerError(true));
  };

  const openCard = () =>
    dispatch(
      openDialog({
        children: (
          <div className="relative">
            <IconButton className="absolute" style={{ top: 8, right: 8 }} onClick={() => dispatch(closeDialog())}>
              <Icon>close</Icon>
            </IconButton>
            <ContactCard contact={cardContact} />
          </div>
        ),
        fullWidth: true,
        maxWidth: 'xs'
      })
    );

  const handleCloseSnackbar = () => {
    setSavedSuccessfully(false);
    setServerError(false);
  };

  if (!form) return <FuseLoading />;

  return (
    <div style={{ marginBottom: '50px' }}>
      <Snackbar
        autoHideDuration={6000}
        open={savedSuccessfully}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        style={{ marginTop: 64 }}
      >
        <Alert severity="success">Profile saved successfully!</Alert>
      </Snackbar>
      <Snackbar
        autoHideDuration={6000}
        open={serverError}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        style={{ marginTop: 56 }}
      >
        <Alert severity="error">A server error occurred. Please try again.</Alert>
      </Snackbar>
      <FusePageCarded
        classes={{
          toolbar: 'p-0',
          header: 'min-h-72 h-72 sm:h-136 sm:min-h-136 text-grey-800',
          topBg: 'light-header',
          contentCard: classes.contentCard
        }}
        header={
          <div className="flex flex-1 w-full items-center justify-between px-16">
            <div className="flex flex-col items-start max-w-full">
              <FuseAnimate animation="transition.slideRightIn" delay={300}>
                <Typography
                  className="normal-case flex items-center sm:mb-12"
                  role="button"
                  color="inherit"
                  onClick={() => history.push('/')}>
                  <Icon className="text-20">{theme.direction === 'ltr' ? 'arrow_back' : 'arrow_forward'}</Icon>
                  <span className="mx-4">Go Back</span>
                </Typography>
              </FuseAnimate>
              <FuseAnimate animation="transition.slideLeftIn" delay={300}>
                <Typography variant="caption">User Profile</Typography>
              </FuseAnimate>
            </div>
            <div className="flex items-center" style={{ gap: 8 }}>
              <Button variant="contained" className="normal-case" startIcon={<Icon>contact_mail</Icon>} onClick={openCard}>
                Digital Card
              </Button>
              {isDirty && (
                <Button variant="contained" color="secondary" onClick={handleSave}>
                  Save
                </Button>
              )}
            </div>
          </div>
        }
        contentToolbar={
          <Tabs
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons="auto"
            value={tabValue}
            onChange={(e, value) => setTabValue(value)}
            classes={{ root: 'w-full h-64' }}
          >
            {/* Broken onto two lines to match the admin profile at user/:id. */}
            <Tab
              className="h-64 normal-case"
              label={
                <span>
                  Personal
                  <br />
                  Information
                </span>
              }
            />
            <Tab
              className="h-64 normal-case"
              label={
                <span>
                  Professional
                  <br />
                  Information
                </span>
              }
            />
            <Tab
              className="h-64 normal-case"
              label={
                <span>
                  Marketing
                  <br />
                  Information
                </span>
              }
            />
            {/* One word, so no line break to match the others — same as the admin
                profile at user/:id. Read-only here: it shows what somebody earns, not
                a place to change it. */}
            <Tab className="h-64 normal-case" label="Financials" />
            <Tab
              className="h-64 normal-case"
              label={
                <span>
                  User
                  <br />
                  Information
                </span>
              }
            />
          </Tabs>
        }
        content={
          <div className="p-16 sm:p-24">
            {tabValue === 0 && (
              <BasicInfoTab
                form={form}
                setForm={setForm}
                handleChange={handleChange}
                setInForm={setInForm}
                classes={classes}
              />
            )}
            {tabValue === 1 && (
              <div
                style={{
                  overflowY: 'auto',
                  boxSizing: 'content-box'
                }}
              >
                <ProfessionalInfoTab form={form} setForm={setForm} handleChange={handleChange} />
              </div>
            )}

            {tabValue === 2 && <MarketingTab form={form} handleChange={handleChange} setInForm={setInForm} />}

            {tabValue === 3 && <FinancialsTab form={form} />}

            {tabValue === 4 && (
              <UserInfoTab
                form={form}
                setForm={setForm}
                handleChange={handleChange}
                setInForm={setInForm}
                savedUsername={loggedUser.username}
              />
            )}
          </div>
        }
        innerScroll
      />
    </div>
  );
}

export default withReducer('settings', reducer)(Userprofile);
